/**
 * Agents v3 — the "Build your own agent" WIZARD: one question per screen,
 * with option chips and a free-text fallback, a progress rail that ticks as
 * answers land, Back/Next, and "Build it" at the end. Nothing here talks to
 * the builder; the parent turns the answers into the intake brief the
 * builder checks (and only then asks follow-ups or proposes).
 *
 * The script itself (questions, options, required gating) lives in
 * `builder-intake.ts`; this component only collects.
 */
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import MainSquareButton from '../../../ui/buttons/MainSquareButton';
import type { AgentCadence } from '../../../lib/api/client-reports/client-agents-api';
import { composeCronSchedule, describeSchedule } from '../../client-reports/agents/agent-helpers';
import SchedulePicker, { SIMPLE_WEEKLY } from '../../client-reports/agents/SchedulePicker';
import {
  ALL_SOURCES,
  INTAKE_ORDER,
  STEP_SHORT,
  answerSummary,
  frequencyPresets,
  intakeText,
  isAnswered,
  metricLabel,
  platformLabel,
  type IntakeAnswers,
  type IntakeStep,
  type IntakeStepId,
} from './builder-intake';

interface Props {
  steps: IntakeStep[];
  answers: IntakeAnswers;
  timezone: string;
  cadences?: AgentCadence[];
  /** Shown by the host (the popup title); kept for callers. */
  brandName?: string;
  onChange: (answers: IntakeAnswers) => void;
  /** Every required answer is in — hand over to the builder. */
  onFinish: (answers: IntakeAnswers) => void;
  /** Leave the questions and just describe it in the chat. */
  onSkip: () => void;
}

interface Draft {
  text: string;
  picks: string[];
  cron: string;
}

const splitList = (text: string): string[] =>
  text
    .split(/[,;\n]+/)
    .map((s) => s.trim())
    .filter(Boolean);

/** The draft for a step, seeded from an existing answer when stepping back. */
function draftFor(step: IntakeStep, answers: IntakeAnswers, fallbackCron: string): Draft {
  const empty: Draft = { text: '', picks: [], cron: fallbackCron };
  switch (step.id) {
    case 'goal':
      return { ...empty, text: answers.goal ?? '' };
    case 'sources': {
      const known = new Set((step.options ?? []).map((o) => o.value));
      const picks = (answers.sources ?? []).filter((v) => known.has(v));
      const typed = (answers.sources ?? []).filter((v) => !known.has(v));
      return { ...empty, picks, text: typed.join(', ') };
    }
    case 'kpis': {
      const known = new Set((step.options ?? []).map((o) => o.value));
      const picks = (answers.kpis ?? []).filter((v) => known.has(v));
      const typed = (answers.kpis ?? []).filter((v) => !known.has(v));
      return { ...empty, picks, text: typed.join(', ') };
    }
    case 'trigger': {
      const isOption = (step.options ?? []).some((o) => o.value === answers.trigger);
      return { ...empty, picks: isOption && answers.trigger ? [answers.trigger] : [], text: isOption ? '' : answers.trigger ?? '' };
    }
    case 'frequency':
      return {
        ...empty,
        cron: answers.frequency?.cron || fallbackCron,
        text: answers.frequency?.cron ? '' : answers.frequency?.text ?? '',
      };
    case 'output': {
      const isOption = (step.options ?? []).some((o) => o.value === answers.output);
      return { ...empty, picks: isOption && answers.output ? [String(answers.output)] : [], text: isOption ? '' : String(answers.output ?? '') };
    }
    case 'extras':
      return { ...empty, text: answers.extras ?? '' };
    default:
      return empty;
  }
}

/** The answer a draft produces for a step (null when the step is unanswered). */
function answerFrom(step: IntakeStep, draft: Draft, timezone: string): IntakeAnswers | null {
  const text = draft.text.trim();
  switch (step.id) {
    case 'goal':
      return text ? { goal: text } : null;
    case 'sources': {
      const list = [...draft.picks, ...splitList(text)];
      return list.length ? { sources: list } : null;
    }
    case 'kpis': {
      const list = [...draft.picks, ...splitList(text)];
      return list.length ? { kpis: list } : null;
    }
    case 'trigger':
      return text ? { trigger: text } : draft.picks[0] ? { trigger: draft.picks[0] } : null;
    case 'frequency':
      if (text) return { frequency: { cron: '', timezone, text } };
      return draft.cron ? { frequency: { cron: draft.cron, timezone } } : null;
    case 'output':
      return text ? { output: text } : draft.picks[0] ? { output: draft.picks[0] } : null;
    case 'extras':
      return { extras: text };
    default:
      return null;
  }
}

const BuilderWizard = ({ steps, answers, timezone, cadences, onChange, onFinish, onSkip }: Props) => {
  const { t } = useTranslation();
  const tx = (text: string) => intakeText(t, text);
  const [index, setIndex] = useState(0);
  const step = steps[Math.min(index, steps.length - 1)];
  const defaultCron = useMemo(() => composeCronSchedule(SIMPLE_WEEKLY), []);
  const [draft, setDraft] = useState<Draft>(() => draftFor(step, answers, defaultCron));
  /** The frequency step: which way the user is answering (a chip/picker or words). */
  const [freqMode, setFreqMode] = useState<'pick' | 'words'>('pick');

  // A new step → a fresh draft, seeded from what was answered before.
  useEffect(() => {
    setDraft(draftFor(step, answers, defaultCron));
    setFreqMode(step.id === 'frequency' && answers.frequency?.text && !answers.frequency.cron ? 'words' : 'pick');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step.id]);

  const isLast = index === steps.length - 1;
  const current = answerFrom(step, freqMode === 'words' ? draft : { ...draft, text: step.id === 'frequency' ? '' : draft.text }, timezone);
  const canNext = step.optional ? true : current !== null;

  const commit = (): IntakeAnswers => {
    const patch = current ?? (step.optional ? { extras: '' } : {});
    const next = { ...answers, ...patch };
    onChange(next);
    return next;
  };

  const next = () => {
    if (!canNext) return;
    const merged = commit();
    if (isLast) {
      onFinish(merged);
      return;
    }
    setIndex((i) => Math.min(i + 1, steps.length - 1));
  };

  const back = () => {
    if (index === 0) return;
    if (current) onChange({ ...answers, ...current });
    setIndex((i) => i - 1);
  };

  const jumpTo = (id: IntakeStepId) => {
    const target = steps.findIndex((s) => s.id === id);
    if (target < 0 || target === index) return;
    // Only answered steps (and the next unanswered one) are reachable.
    const reachable = steps.slice(0, target).every((s) => isAnswered(s.id, answers)) || isAnswered(id, answers);
    if (!reachable) return;
    if (current) onChange({ ...answers, ...current });
    setIndex(target);
  };

  const togglePick = (value: string) => {
    setDraft((d) => {
      if (!step.multi) return { ...d, picks: d.picks[0] === value ? [] : [value], text: '' };
      if (value === ALL_SOURCES) return { ...d, picks: d.picks.includes(ALL_SOURCES) ? [] : [ALL_SOURCES] };
      const without = d.picks.filter((v) => v !== value && v !== ALL_SOURCES);
      return { ...d, picks: d.picks.includes(value) ? without : [...without, value] };
    });
  };

  /** A finished step's answer as its tooltip, in the user's language. */
  const shownSummary = (id: IntakeStepId): string => {
    if (id === 'sources') {
      return (answers.sources ?? [])
        .map((s) =>
          s === ALL_SOURCES ? tx('All connected platforms') : platformLabel(s)
        )
        .join(', ');
    }
    if (id === 'kpis') {
      return (answers.kpis ?? []).map((m) => tx(metricLabel(m))).join(', ');
    }
    if (id === 'frequency' && answers.frequency?.cron) {
      return describeSchedule(
        answers.frequency.cron,
        answers.frequency.timezone,
        t
      );
    }
    return tx(answerSummary(id, answers));
  };

  const presets = frequencyPresets(cadences);
  const schedulePreview = draft.cron ? describeSchedule(draft.cron, timezone, t) : '';

  return (
    <div className='agentsHub__wizard' data-testid='builder-wizard'>
      {/* ---- the stepper: one row, seven dots, click to go back ---- */}
      <ol className='agentsHub__stepper' data-testid='builder-checklist' aria-label={t('Steps')}>
        {INTAKE_ORDER.map((id, i) => {
          const done = isAnswered(id, answers);
          const active = step.id === id;
          return (
            <li
              key={id}
              className={`agentsHub__stepper-item${done ? ' agentsHub__stepper-item--done' : ''}${
                active ? ' agentsHub__stepper-item--active' : ''
              }`}
            >
              <button
                type='button'
                className='agentsHub__stepper-btn'
                onClick={() => jumpTo(id)}
                aria-current={active ? 'step' : undefined}
                title={done && !active ? shownSummary(id) : undefined}
              >
                <span className='agentsHub__stepper-mark' aria-hidden='true'>
                  {done ? '✓' : i + 1}
                </span>
                <span className='agentsHub__stepper-label'>{tx(STEP_SHORT[id])}</span>
              </button>
            </li>
          );
        })}
      </ol>

      {/* ---- the current question ---- */}
      <section className='agentsHub__wizard-panel' aria-live='polite'>
        <span className='client-agents__intake-step'>
          {t('AgentWizardStepOf', { current: index + 1, total: steps.length })}
          {step.optional ? t('AgentWizardOptionalSuffix') : ''}
        </span>
        <h3 className='agentsHub__wizard-question'>{tx(step.question)}</h3>
        {step.hint && <p className='client-agents__muted'>{tx(step.hint)}</p>}

        {step.kind === 'text' && (
          <>
            {(step.examples ?? []).length > 0 && (
              <div className='client-agents__chips' aria-label={t('AgentWizardSuggestions')}>
                <span className='client-agents__chips-label'>{t('AgentWizardTry')}</span>
                {(step.examples ?? []).map((example) => (
                  <button
                    key={example}
                    type='button'
                    className='client-agents__chip-btn'
                    onClick={() => setDraft((d) => ({ ...d, text: tx(example) }))}
                  >
                    {tx(example)}
                  </button>
                ))}
              </div>
            )}
            <textarea
              className='client-agents__textarea'
              aria-label={tx(step.question)}
              placeholder={tx(step.placeholder)}
              value={draft.text}
              onChange={(e) => setDraft((d) => ({ ...d, text: e.target.value }))}
            />
          </>
        )}

        {step.kind === 'choice' && (
          <>
            <div className='client-agents__chips' role='group' aria-label={tx(step.question)}>
              {(step.options ?? []).map((opt) => {
                const on = draft.picks.includes(opt.value);
                return (
                  <button
                    key={opt.value}
                    type='button'
                    className={`client-agents__chip-btn${on ? ' client-agents__chip-btn--on' : ''}`}
                    aria-pressed={on}
                    title={opt.hint && tx(opt.hint)}
                    onClick={() => togglePick(opt.value)}
                  >
                    {tx(opt.label)}
                    {opt.hint && <small>{tx(opt.hint)}</small>}
                  </button>
                );
              })}
              {(step.options ?? []).length === 0 && (
                <span className='client-agents__muted'>{t('AgentWizardNothingToPick')}</span>
              )}
            </div>
            <input
              type='text'
              className='client-agents__input'
              aria-label={t('AgentWizardInYourOwnWords', { question: tx(step.question) })}
              placeholder={tx(step.placeholder)}
              value={draft.text}
              onChange={(e) => setDraft((d) => (step.multi ? { ...d, text: e.target.value } : { ...d, text: e.target.value, picks: [] }))}
            />
          </>
        )}

        {step.kind === 'schedule' && (
          <>
            <div className='client-agents__chips' aria-label={t('AgentWizardQuickSchedules')}>
              {presets.map((p) => {
                const on = freqMode === 'pick' && draft.cron === p.cron;
                return (
                  <button
                    key={p.cron}
                    type='button'
                    className={`client-agents__chip-btn${on ? ' client-agents__chip-btn--on' : ''}`}
                    aria-pressed={on}
                    onClick={() => {
                      setFreqMode('pick');
                      setDraft((d) => ({ ...d, cron: p.cron, text: '' }));
                    }}
                  >
                    {tx(p.label)}
                  </button>
                );
              })}
            </div>
            <div className='client-agents__intake-picker'>
              <SchedulePicker
                cron={draft.cron}
                timezone={timezone}
                allowedCadences={cadences}
                idPrefix='builder-wizard'
                onChange={(cron) => {
                  setFreqMode('pick');
                  setDraft((d) => ({ ...d, cron, text: '' }));
                }}
              />
            </div>
            <input
              type='text'
              className='client-agents__input'
              aria-label={t('AgentWizardInYourOwnWords', { question: tx(step.question) })}
              placeholder={tx(step.placeholder)}
              value={draft.text}
              onChange={(e) => {
                const text = e.target.value;
                setFreqMode(text.trim() ? 'words' : 'pick');
                setDraft((d) => ({ ...d, text }));
              }}
            />
            <p className='client-agents__muted' data-testid='wizard-frequency-preview'>
              {freqMode === 'words' && draft.text.trim()
                ? t('AgentWizardInYourWordsPreview', { text: draft.text.trim() })
                : schedulePreview}
            </p>
          </>
        )}

      </section>

      {/* ---- one footer row ---- */}
      <div className='agentsHub__wizard-actions'>
        <button type='button' className='client-agents__intake-skip' onClick={onSkip}>
          {t('AgentWizardSkipQuestions')}
        </button>
        <div className='agentsHub__wizard-actions-right'>
          {!canNext && <span className='client-agents__muted'>{t('AgentWizardPickToContinue')}</span>}
          {index > 0 && <MainSquareButton type='button' value={t('AgentWizardBack')} buttonType='light' normal onClick={back} />}
          <MainSquareButton
            type='button'
            value={isLast ? t('AgentWizardBuildIt') : step.optional && current === null ? t('AgentWizardSkip') : t('AgentWizardNext')}
            buttonType='success'
            normal
            disabled={!canNext}
            onClick={next}
          />
        </div>
      </div>
    </div>
  );
};

export default BuilderWizard;
